import { useEffect, useLayoutEffect, useMemo } from "react";
import "./styles/Global.scss";
import AppRouter from "./router/AppRouter";
import {
  displayNamesRequest,
  displayNamesUpdate,
} from "./store/actions/displayNames";
import { useDispatch, useSelector } from "react-redux";
import { StoreState } from "./store/configureStore";
import { ConfigProvider, message, notification } from "antd";
import * as Sentry from "@sentry/react";
import { switchAntdLocale } from "./utils/switchLocale";
import {
  notificationAdd,
  notificationsRequest,
} from "./store/actions/notifications";
import { antdTheme, currentPlatform, defaultHeaders, platformInfo, projectType } from "./constants";
import { refreshTokenRequest, setTwoFaStatus } from "./store/actions/login";
import { teamsRequest } from "./store/actions/teams";
import { fetchProjectListRequest } from "./store/actions/projects";
import mixpanel from "mixpanel-browser";
import { celsureDisplayNamesRequest } from "./store/actions/celsure";
import graphql from "babel-plugin-relay/macro";
import { useSubscription } from "react-relay";
import {
  modelsConfigListRequest,
} from "./store/actions/formulate";
import { enabledDomains } from "src";
import "react-datasheet-grid/dist/style.css";
import { unitListRequest } from "./store/actions/conversion";
import jwtManager from "./utils/jwtManager";
import { LoginProgress } from "./store/reducers/login";
import { setDefaultHeaders } from "./store/actions/defaultHeaders";
import { BroadcastChannel } from "broadcast-channel";
import { useDVCClient, useVariableValue, withDVCProvider } from "@devcycle/devcycle-react-sdk";
import { configsRequest } from "./store/actions/configs";
import { Userpilot } from "userpilot";
import { useLocation } from "react-router-dom";
import { authMagicLinkRequest } from "./store/actions/loginViaMagicLink";
import { fetchLabsLocationRequest } from "./store/actions/settings";
import { useSessionStorage } from "./utils/hooks/useSessionStorage";
import { useNotifySocket } from "./utils/hooks/useNotifySocket";
import { useToken } from "./utils/hooks/useToken";
import { useDarkreader } from "react-darkreader";
import { tdsIngredientsUploadSuccessNotificationReceived } from "./store/actions/inventoryV2";
import { useSync } from "./mocks/useSync";
import { languageToggle } from "./store/actions/language";

export const bool = (flag: any) => flag === "true"

const appSubscription = graphql`
  subscription AppSubscription($company_id: String!) {
    displayNames(company_id: $company_id) {
      type
      data {
        name
        unit
        category
        identifier
        property_type
      }
    }
  }
`;

const notificationSubscription = graphql`
  subscription AppNotificationSubscription($user_id: String!) {
    notifications(user_id: $user_id) {
      data {
        message {
          en {
            message {
              receivers_message
              description
              senders_message
            }
          }
          fr {
            message {
              receivers_message
              description
              senders_message
            }
          }
          ja {
            message {
              receivers_message
              description
              senders_message
            }
          }
          de {
            message {
              receivers_message
              description
              senders_message
            }
          }
          ko {
            message {
              receivers_message
              description
              senders_message
            }
          }
        }
        notification_type
        created
        notification_id
        is_read
        download_link
        work_order_id
        sender
        receiver
      }
    }
  }
`;

export const tokenBroadcast = new BroadcastChannel("token_channel");

type LocaleCompareFunction = (
  compareString: string,
  locales?: string | string[] | undefined,
  options?: Intl.CollatorOptions | undefined
) => number;

// Store the original method
const originalLocaleCompare: LocaleCompareFunction = String.prototype.localeCompare;

// Declare the modification to String prototype
declare global {
  interface String {
    localeCompare(
      compareString: string,
      locales?: string | string[] | undefined,
      options?: Intl.CollatorOptions | undefined
    ): number;
  }
}

// Override the method
// eslint-disable-next-line no-extend-native
String.prototype.localeCompare = function (
  this: string,
  compareString: string,
  locales?: string | string[] | undefined,
  options?: Intl.CollatorOptions | undefined
): number {
  options = { numeric: true, sensitivity: "base", ...options }

  return originalLocaleCompare.call(this, compareString, locales, options);
};

function App() {
  const tokenData = useToken()
  const { progressBar } = useSync({ autoSync: true })
  const dispatch = useDispatch();
  const currentLanguage = useSelector(
    (state: StoreState) => state.language.current
  );
  const locale = useSelector((state: StoreState) => state.language.current);

  const antdLocale = useMemo(() => {
    return switchAntdLocale(locale);
  }, [locale]);

  useEffect(() => {
    dispatch(languageToggle(currentLanguage))
  }, [currentLanguage, dispatch])

  const {
    user_loggedin_IP,
    user_id,
    user_name,
    user_email,
    company,
    company_id,
    created,
    image_url,
    project_type,
  } = useSelector<StoreState, any>(
    (state: StoreState) => state.login.loginResponse
  );
  const { tokenStatus, isLoginComplete } = useSelector(
    (state: StoreState) => state.login
  );
  const headers = useSelector((state: StoreState) => state.defaultHeaders);
  const tokenValid = useMemo(
    () => !!jwtManager.getToken() && !!user_id,
    [user_id]
  );
  const token = jwtManager.getToken();

  const location = useLocation();
  const getSessionStorage = useSessionStorage().getSessionStorage
  const deleteSessionStorage = useSessionStorage().deleteSessionStorage

  useEffect(() => {
    const onMessage = (message: any) => {
      if (!!user_id) {
        jwtManager.setToken(message);
        // window.indexedDB.deleteDatabase(
        //   "pubkey.broadcast-channel-0-token_channel"
        // );
      }
    }
    tokenBroadcast.addEventListener("message", onMessage)
    return () => {
      tokenBroadcast.removeEventListener("message", onMessage)
    };
  }, [user_id])

  useEffect(() => {
    if (enabledDomains.includes(window.location.host)) {
      mixpanel.track_pageview()
    }
  }, [
    location.pathname
  ]);

  useEffect(() => {
    if (enabledDomains.includes(window.location.host)) {
      mixpanel.identify(user_id);
      mixpanel.people.set({
        $avatar: image_url,
        $name: user_name,
        $email: user_email,
        company: company,
      });
    }
  }, [user_id, user_name, user_email, user_loggedin_IP, company, image_url]);

  useEffect(() => {
    if (enabledDomains.includes(window.location.host)) {
      (window as any).clarity("set", "email", user_email);
      (window as any).clarity("set", "company", company);
      Sentry.setUser({
        id: user_id,
        username: user_name,
        email: user_email,
        ip_address: user_loggedin_IP,
      });
      Sentry.setTag("user_name", user_name);
      Sentry.setTag("company", company);
    }
  }, [user_id, user_name, user_email, user_loggedin_IP, company, image_url]);

  useEffect(() => {
    if (currentPlatform) {
      document.title = platformInfo[currentPlatform].name;
    }
  }, []);

  const dvcClient = useDVCClient();

  useEffect(() => {
    if (user_id) {
      const newUser = {
        user_id,
        email: user_email,
        customData: {
          company_id
        }
      };

      dvcClient.identifyUser(newUser);
    }
  }, [user_id, user_email, dvcClient, company_id]);

  useEffect(() => {
      const userDetails = {
        email: user_email,
        user_id: user_id,
        name: user_name,
        created_at: created,
        company: {
          company_name: company,
          company_id: company,
        },
      };
      const setDetails: Boolean = !!(
        user_email &&
        user_id &&
        user_name &&
        created &&
        company
      );
      (window as any).Intercom("boot", {
        app_id: "xumvd7ya",
        language_override: currentLanguage === "zh" ? "zh-CN" : currentLanguage,
        ...(setDetails ? userDetails : {}),
        hide_default_launcher: !token,
      });
  }, [company, created, user_email, user_id, user_name, currentLanguage, token]);

  useEffect(() => {
    dispatch(
      setDefaultHeaders({
        ...defaultHeaders,
        lang: currentLanguage,
        platform: currentPlatform,
      })
    );
  }, [currentLanguage, dispatch]);

  useEffect(() => {
    if (
      isLoginComplete === LoginProgress.COMPLETE &&
      !jwtManager.getToken() &&
      !!user_id
    ) {
      dispatch(refreshTokenRequest());
    }
  }, [dispatch, tokenValid, isLoginComplete, user_id]);

  useEffect(() => {
    const location = window.location
    if (location?.pathname?.includes("/magic/")) {
      dispatch(authMagicLinkRequest({ token: location?.pathname.replace("/magic/", ""), device_id: "uuid4+uuid4" }))
    }
  }, [dispatch])

  useEffect(() => {
    tokenStatus &&
      !!jwtManager.getToken() &&
      dispatch(displayNamesRequest({ inputs: [] }));
  }, [locale, dispatch, tokenStatus, headers]);

  useEffect(() => {
    if (tokenStatus && !!jwtManager.getToken()) {
      dispatch(configsRequest());
      dispatch(notificationsRequest());
      dispatch(teamsRequest());
      dispatch(unitListRequest({}));
      dispatch(fetchLabsLocationRequest())
    }
  }, [dispatch, tokenStatus]);

  useEffect(() => {
    const isTokenAvailable = tokenStatus && !!jwtManager.getToken();
    if (isTokenAvailable) {
      dispatch(fetchProjectListRequest({ set: true }));
    }
    if (project_type === projectType.celsure && isTokenAvailable) {
      dispatch(celsureDisplayNamesRequest());
    }
  }, [dispatch, project_type, tokenStatus]);

  useEffect(() => {
    const isTokenAvailable = tokenStatus && !!jwtManager.getToken();
    if (isTokenAvailable) {
      const isLoginWithFirebaseInitiated = getSessionStorage("isLoginWithFirebaseInitiated")
      if (isLoginWithFirebaseInitiated) {
        deleteSessionStorage("isLoginWithFirebaseInitiated")
      }
    }
  }, [tokenStatus, getSessionStorage, deleteSessionStorage])

  useEffect(() => {
    return () => {
      notification.destroy();
      message.destroy();
    };
  }, [token]);

  const config = useMemo(
    () => ({
      variables: { company_id },
      subscription: appSubscription,
      onNext: (msg: any) => {
        dispatch(displayNamesUpdate(msg));
      },
    }),
    [company_id, dispatch]
  );
  useSubscription(config);

  //Notifications Subscription
  const notificationsConfig = useMemo(
    () => ({
      variables: { user_id },
      subscription: notificationSubscription,
      onNext: (msg: any) => {
        if (Object.keys(msg?.notifications?.data || {}).length) {
          const response = msg?.notifications?.data;
          switch (response?.notification_type) {
            case "2fa_status":
              dispatch(setTwoFaStatus(true));
              break;
            case "customML":
              dispatch(modelsConfigListRequest());
              break;
          }
          if (response?.receiver?.includes(user_id)) {
            notification.success({
              message:
                response?.message?.[currentLanguage]?.message
                  ?.receivers_message,
              description:
                response?.message?.[currentLanguage]?.message?.description ||
                null,
              duration: 8,
            });
          }
          dispatch(
            notificationAdd({
              ...response,
              message: response?.message?.[currentLanguage]?.message,
            })
          );
        }
      },
    }),
    [user_id, currentLanguage, dispatch]
  );
  useSubscription(notificationsConfig);

  // const dvcReady = useIsDVCInitialized()

  // const variables = dvcClient.allVariables()
  // console.log("🚀 ~ file: App.tsx:298 ~ App ~ variables:", variables)

  // useEffect(() => {
  // 	if (dvcReady) {
  // 		if (localStorage.getItem("version")) {
  // 			if (version !== localStorage.getItem("version")) {
  // 				// if (window.confirm(`🎉 Update to version ${version}!\n\nCaution: All unsaved changes will be lost if you choose proceed.`)) {
  // 				localStorage.setItem("version", version)
  // 				// window.location.reload();
  // 				// }
  // 			}
  // 		} else {
  // 			localStorage.setItem("version", version)
  // 		}
  // 	}
  // }, [version, dvcReady])

  const isSIT = process.env.REACT_APP_ENV === "sit";
  useEffect(() => {
    if (user_id && isSIT) {
      Userpilot.identify(user_id, {
        name: user_name,
        email: user_email,
      });
    }
  }, [isSIT, user_email, user_id, user_name]);

  useEffect(() => {
    if (user_id && isSIT) {
      Userpilot.reload();
    }
  }, [user_id, location, isSIT]);

  useLayoutEffect(() => {
    window.addEventListener("beforeunload", () => { localStorage.setItem("reloaded", "true") })
  }, [])

  const socket = useNotifySocket("notify", tokenData)

  useEffect(() => {
    const onNewNotification = (_notification: any) => {
      message.info(_notification?.data?.message?.receivers_message)
      dispatch(notificationsRequest())
    }
    socket?.on("notification", onNewNotification)

    const tdsNotification = (_notification: any) => {
      if (_notification?.status === "Success") {
        notification.success({
          message: _notification?.message
        })
        dispatch(tdsIngredientsUploadSuccessNotificationReceived(true))
      } else {
        notification.error({
          message: _notification?.message
        })
      }
    }

    socket?.on("tds-notification", tdsNotification)


    return () => {
      socket?.off("notification", onNewNotification)
      socket?.disconnect()
    }
  }, [dispatch, socket])

  const [isDark, { toggle }] = useDarkreader(bool(localStorage?.getItem?.("isDark")), { contrast: 100, darkSchemeBackgroundColor: "#1A1C24" })

  const enableTickets = useVariableValue("ticketing-system", false)

  useEffect(() => {
    if (enableTickets) {
      fetch("https://api.devrev.ai/auth-tokens.create", {
        method: "POST",
        headers: {
          "Accept": "accept: application/json, text/plain, */*",
          "Content-Type": "application/json",
          "Authorization": `Bearer eyJhbGciOiJSUzI1NiIsImlzcyI6Imh0dHBzOi8vYXV0aC10b2tlbi5kZXZyZXYuYWkvIiwia2lkIjoic3RzX2tpZF9yc2EiLCJ0eXAiOiJKV1QifQ.eyJhdWQiOlsiamFudXMiXSwiYXpwIjoiZG9uOmlkZW50aXR5OmR2cnYtdXMtMTpkZXZvLzE3cjk0blpRWjpkZXZ1LzEiLCJleHAiOjE5MDY1OTM0NDgsImh0dHA6Ly9kZXZyZXYuYWkvY2xpZW50aWQiOiJkb246aWRlbnRpdHk6ZHZydi11cy0xOmRldm8vMTdyOTRuWlFaOnN2Y2FjYy8xNyIsImh0dHA6Ly9kZXZyZXYuYWkvZGV2b19kb24iOiJkb246aWRlbnRpdHk6ZHZydi11cy0xOmRldm8vMTdyOTRuWlFaIiwiaHR0cDovL2RldnJldi5haS9kZXZvaWQiOiJERVYtMTdyOTRuWlFaIiwiaHR0cDovL2RldnJldi5haS9zdmNhY2MiOiJkb246aWRlbnRpdHk6ZHZydi11cy0xOmRldm8vMTdyOTRuWlFaOnN2Y2FjYy8xNyIsImh0dHA6Ly9kZXZyZXYuYWkvdG9rZW50eXBlIjoidXJuOmRldnJldjpwYXJhbXM6b2F1dGg6dG9rZW4tdHlwZTphYXQiLCJpYXQiOjE3NDg5MTM0NDgsImlzcyI6Imh0dHBzOi8vYXV0aC10b2tlbi5kZXZyZXYuYWkvIiwianRpIjoiZG9uOmlkZW50aXR5OmR2cnYtdXMtMTpkZXZvLzE3cjk0blpRWjp0b2tlbi9sTE00VTVzSSIsInN1YiI6ImRvbjppZGVudGl0eTpkdnJ2LXVzLTE6ZGV2by8xN3I5NG5aUVo6c3ZjYWNjLzE3In0.vqaVuMv7OULtZ3Zg4MLm93_0El335yX0-Z7ABFndOBKAFxVJyEktoWxK_ZQ6aicCRsVmrQO_hL401TPwPw6kKX8GmW1usA4NXh1LiVTuc3ll8iT5Rxy2PpoGJ5r17AMGNxFWrnC5L29g6GHQFBufNVnNb35VdLe396s4FTmAJUzKoodH740c3sPD-IG2dbUdKEkD2ztnPJgDBPhwwQXwjnIyPW3a00oVjuj3JMJAtAgz9BupgMKV5p0y7z6Qf77MTtg8leAfWWnS1MlbHYB5Hn8weehgQTAiJ1bHcoVdKwgmWoSEOzWCQXcaDn7AwxXeitA2zCaBUuMXm-I5yXbmyg`,
        },
        body: JSON.stringify({
          "rev_info": {
            "user_ref": user_id,
            "account_ref": company_id,
            // "workspace_ref": "devrev-dev",
            "user_traits": {
              "email": user_email,
              "display_name": user_name,
              // "phone_numbers": ["+911122334455"],
              // "custom_fields": {
              //   "tnt__<field1_name>": "value 1"
              // }
            },
            // "workspace_traits": {
            //   "display_name": "Devrev Dev",
            //   "custom_fields": {}
            // },
            "account_traits": {
              "display_name": company,
              // "domains": [
              //   "devrev.ai"
              // ],
              // "custom_fields": {
              //   "tnt__<field2_name>": "value x"
              // }
            }
          }
        })
      })
        .then(response => response.json())
        .then(data => {
          if (data?.access_token) {
            console.log("Plug SDK initialized successfully");
            (window as any).plugSDK.init({
              // Please ensure you replace the app_id with your unique app id
              app_id: "don:core:dvrv-us-1:devo/17r94nZQZ:plug_setting/1",
              session_token: data?.access_token,
              // disable_plug_chat_window: !enableTickets,
            });
          } else {
            console.error("Failed to initialize Plug SDK:", data);
          }
        })
        .catch(error => {
          console.error("Error initializing Plug SDK:", error);
        });
    }

  }, [enableTickets, user_id, user_email, user_name, company_id, company]);

  return (
    <ConfigProvider locale={antdLocale} theme={
      {
        "token": antdTheme
      }
    }
      typography={{
        style: {
          margin: 0,
        }
      }}
    >
      {progressBar}
      <AppRouter isDark={isDark} toggleDarkTheme={toggle} />
    </ConfigProvider>
  );
}

export default withDVCProvider({
  sdkKey: process.env.REACT_APP_DVC_CLIENT_SDK_KEY ?? "",
})(App);
